import { useEffect, useState } from "react";
import FunnelRenderer from "@/features/funnels/pages/FunnelRenderer";
import { Loader2 } from "lucide-react";

export function DomainHandler({ children }: { children: React.ReactNode }) {
    const [loading, setLoading] = useState(true);
    const [funnelId, setFunnelId] = useState<string | null>(null);

    useEffect(() => {
        const checkDomain = async () => {
            const hostname = window.location.hostname;
            // Allow testing via query param
            const urlParams = new URLSearchParams(window.location.search);
            const testDomain = urlParams.get('domain');

            const domainToCheck = testDomain || hostname;

            // Skip check for localhost and main app domains (add your production domains here)
            if (domainToCheck === 'localhost' || domainToCheck === '127.0.0.1' || domainToCheck.endsWith('.vercel.app')) {
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`/api/funnels/domain/${domainToCheck}`);
                const json = await res.json();

                if (json.data) {
                    setFunnelId(json.data.id);
                }
            } catch (error) {
                console.error("Domain check failed", error);
            } finally {
                setLoading(false);
            }
        };

        checkDomain();
    }, []);

    if (loading) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-white">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (funnelId) {
        // If we found a funnel for this domain, render it directly
        // We need to pass the funnelId to the renderer. 
        // Note: FunnelRenderer usually takes params from URL, so we might need to adjust it or mock the router context.
        // A simpler way is to redirect to the /f/ route, but that changes the URL which defeats the purpose of custom domain.
        // So we should render it here.

        // We need to mock the useParams hook or pass props to FunnelRenderer
        // Let's modify FunnelRenderer to accept props as override.
        return <FunnelRendererOverride funnelId={funnelId} />;
    }

    return <>{children}</>;
}

// Wrapper to inject params into FunnelRenderer logic
import { useParams } from "react-router-dom";
// We can't easily override useParams. 
// Instead, let's make a wrapper that sets the context or just modifies FunnelRenderer to accept props.
// For now, let's assume FunnelRenderer can take props. I'll update FunnelRenderer next.

function FunnelRendererOverride({ funnelId }: { funnelId: string }) {
    // We can't use the normal router params here because the URL is just "/"
    // So we need to handle the routing manually or use a memory router?
    // Actually, if we are at root "/", we want to show the first step.
    // If we are at "/step-slug", we show that step.

    // This requires a bit more complex routing setup for custom domains.
    // For MVP, let's just render the FunnelRenderer and let it handle the logic.
    // I will update FunnelRenderer to accept an optional `funnelIdProp`.
    return <FunnelRenderer funnelIdProp={funnelId} />;
}
